import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowDownIcon, CheckCircleIcon, XCircleIcon, ChevronDownIcon, ChevronUpIcon, ShieldCheckIcon, DatabaseIcon, LayoutDashboardIcon, TargetIcon, CodeIcon, WrenchIcon, ServerIcon, BarChart3Icon } from 'lucide-react';
import { ServiceNowSubNav } from '../components/ServiceNowSubNav';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5,
      ease: 'easeOut'
    }
  })
};
const governanceCards = [{
  icon: <LayoutDashboardIcon className="h-6 w-6 text-nrs-orange" data-id="element-2676" data-mp="2umxqo4tm2h38" />,
  title: 'Technical Policy Governance',
  desc: 'Architecture standards, code review, upgrade-safe design'
}, {
  icon: <DatabaseIcon className="h-6 w-6 text-nrs-orange" data-id="element-2677" data-mp="3men4tqvv2j19" />,
  title: 'Data Governance',
  desc: 'Master data ownership, integration patterns, secure instance sync'
}, {
  icon: <ShieldCheckIcon className="h-6 w-6 text-nrs-orange" data-id="element-2678" data-mp="2fnnm64xpvvwo" />,
  title: 'Security & ATO Governance',
  desc: 'Continuous ATO readiness, RMF compliance, security control validation'
}, {
  icon: <TargetIcon className="h-6 w-6 text-nrs-orange" data-id="element-2679" data-mp="2amq37q4vuxu4" />,
  title: 'Strategic Governance',
  desc: 'Application rationalization, business case validation, ROI measurement'
}];
const developmentCards = [{
  icon: <CodeIcon className="h-6 w-6 text-nrs-blue" data-id="element-2680" data-mp="2ye59tv3p01b6" />,
  title: 'Platform Development',
  desc: 'ITSM, HRSD, SPM, Security Operations, custom applications'
}, {
  icon: <WrenchIcon className="h-6 w-6 text-nrs-blue" data-id="element-2681" data-mp="2uc0gyyl2j8pl" />,
  title: 'Specialized Development',
  desc: 'Complex integrations, custom modules, secure on-premise solutions'
}, {
  icon: <ServerIcon className="h-6 w-6 text-nrs-blue" data-id="element-2682" data-mp="33e9rvpk9143i" />,
  title: 'Platform Operations',
  desc: 'Day-to-day administration, ongoing enhancements, support'
}, {
  icon: <BarChart3Icon className="h-6 w-6 text-nrs-blue" data-id="element-2683" data-mp="18z76o428g86e" />,
  title: 'Application Migration',
  desc: 'Legacy system consolidation, data migration, integration buildout'
}];
const governanceGaps = [{
  title: 'Technical Policy Governance',
  items: ['No architecture review process before development starts', 'No coding standards or best practices enforcement', 'No upgrade-safety validation', 'Heavy customization creating technical debt']
}, {
  title: 'Data Governance',
  items: ['No master data ownership across integrations', 'Inconsistent data models between modules', 'No strategy for cross-domain sync across secure instances', 'Integration sprawl with no patterns or standards']
}, {
  title: 'Security & ATO Governance',
  items: ['No pre-change security impact analysis', 'Developers introducing changes that jeopardize platform ATO', 'No continuous RMF compliance monitoring', 'ATO re-authorization delays due to undocumented changes']
}, {
  title: 'Strategic Governance',
  items: ['No independent application rationalization', 'No objective business case validation for new modules', 'No ROI measurement framework', 'SIs incentivized to build everything regardless of fit']
}];
const consequences = [{
  icon: '🔧',
  label: 'Technical Debt',
  desc: 'Customizations that block upgrades and create security vulnerabilities'
}, {
  icon: '💸',
  label: 'Wasted Investment',
  desc: "Building applications on ServiceNow that shouldn't be there"
}, {
  icon: '🔗',
  label: 'Integration Chaos',
  desc: '50+ point-to-point integrations with no governance'
}, {
  icon: '⚠️',
  label: 'ATO Jeopardy',
  desc: 'Platform changes threatening accreditation status'
}, {
  icon: '📊',
  label: 'Compliance Risk',
  desc: 'Inability to demonstrate data lineage or security posture'
}];
const comparisonRows = [{
  traditional: 'Impact provides product vision only; SI provides development only',
  nrs: 'We provide BOTH governance AND development'
}, {
  traditional: 'Large SI builds whatever you approve with no independent validation',
  nrs: 'We build WITH built-in governance (self-governing development)'
}, {
  traditional: 'No independent application rationalization',
  nrs: 'We assess your entire portfolio objectively BEFORE building'
}, {
  traditional: 'Separate vendors for governance vs. development creates friction',
  nrs: 'Single partner for both = alignment and accountability'
}, {
  traditional: 'Focus on feature delivery without governance',
  nrs: 'Every project includes architecture review, data governance, and ATO validation'
}];
const proofPoints = [{
  metric: 'Application rationalization ROI',
  result: '3:1 average savings'
}, {
  metric: 'Applications evaluated',
  result: '50-150 per engagement; 30-40% strong fit'
}, {
  metric: 'Legacy decommissioning savings',
  result: '$200K-600K annual savings'
}, {
  metric: 'Technical policy compliance',
  result: '95%+ first-submission approval'
}, {
  metric: 'Data governance maturity',
  result: '100% master data ownership in 90 days'
}, {
  metric: 'Upgrade readiness improvement',
  result: 'Instance Scan: 60s → 85+'
}, {
  metric: 'Cross-instance sync accuracy',
  result: '<2% data discrepancy rate'
}, {
  metric: 'Migration fit accuracy',
  result: '<5% regret rate on decisions'
}];
const faqs = [{
  q: 'How is this different from ServiceNow Impact?',
  a: 'Impact provides product-line vision and adoption roadmaps for ITSM, HRSD, SPM, etc. We provide the governance layer: technical policy (architecture standards), data governance (master data ownership, integration patterns), and strategic governance (application rationalization, business case validation). We\'re complementary—Impact drives the "what\'s possible," we ensure the "how to do it right and whether you should."'
}, {
  q: "Won't this slow down our SI's development work?",
  a: 'Initially, yes—architecture review adds 1-2 days per project. But it prevents 3-6 month rework cycles when customizations break upgrades or integrations fail. Net result: 30-40% faster time-to-value because you build the right thing once instead of the wrong thing three times.'
}, {
  q: "What if your application rationalization says we SHOULDN'T migrate something to ServiceNow?",
  a: "That's the point. We're independent. If an application is a poor fit (specialized functionality, better standalone tools exist, ROI is negative), we'll tell you. Unlike SIs who profit from migration work, our incentive is accuracy, not billable hours."
}, {
  q: 'Can we still work with our current SI?',
  a: 'Absolutely. We complement your SI—they do the implementation work, we do the governance oversight. Think of it as an independent code review process. Good SIs actually appreciate having clear architecture standards and governance processes.'
}, {
  q: 'Do you replace ServiceNow Impact consultants?',
  a: 'No. Impact focuses on product adoption and strategic vision for specific modules (ITSM, HRSD, etc.). We focus on technical policy governance across ALL modules, data governance for integrations, and strategic governance for application portfolio rationalization. We coordinate with Impact, not compete.'
}, {
  q: "What if we're on an old version and stuck?",
  a: "That's a governance failure (technical policy gap). We'll inventory technical debt, score business risk, create an upgrade roadmap, and establish architecture standards to prevent it from happening again. Upgrade-proofing is a core competency."
}, {
  q: 'How do you measure application rationalization success?',
  a: 'Three ways: (1) ROI accuracy—do migrated applications deliver predicted savings? (2) Fit accuracy—regret rate on migration decisions. (3) Decommissioning savings—how much did we save from retiring legacy systems vs. migrating them?'
}];
function FAQItem({
  q,
  a
}: {
  q: string;
  a: string;
}) {
  const [open, setOpen] = useState(false);
  return <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2684" data-mp="1ugnz0docnlfp">
      <button className="w-full flex items-center justify-between px-6 py-4 text-left bg-white hover:bg-gray-50 transition-colors" onClick={() => setOpen(!open)} data-id="element-2685" data-mp="1piw3l9ohxh6r">
        <span className="font-bold text-gray-900 text-sm md:text-base pr-4" data-id="element-2686" data-mp="39e8p7f6e4xrs">
          {q}
        </span>
        {open ? <ChevronUpIcon className="h-5 w-5 text-nrs-blue flex-shrink-0" data-id="element-2687" data-mp="3df2b6309w2i1" /> : <ChevronDownIcon className="h-5 w-5 text-nrs-blue flex-shrink-0" data-id="element-2688" data-mp="2zgksijp85g6z" />}
      </button>
      {open && <div className="px-6 py-4 bg-nrs-light border-t border-gray-100" data-id="element-2689" data-mp="34f5kjvubotkc">
          <p className="text-gray-700 text-sm leading-relaxed" data-id="element-2690" data-mp="34h8j549jjv0o">{a}</p>
        </div>}
    </div>;
}
export function ServiceNowHomePage() {
  return <div className="w-full" data-id="element-2691" data-mp="2uhhon2kzdg0l">
      <ServiceNowSubNav data-id="element-2692" data-mp="2uissf2efenrp" />

      {/* HERO */}
      <section className="section-navy hero-grid-pattern" data-id="element-2693" data-mp="2gptaktr68hgz">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-2694" data-mp="2grnzsk9h6vqv">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-2695" data-mp="2lpz82qk4gy6z">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-2696" data-mp="26r59p7rbij0i">
              <div className="inline-block px-3 py-1 bg-nrs-orange/20 border border-nrs-orange/40 rounded-full text-nrs-orange text-xs font-semibold uppercase tracking-wider mb-4" data-id="element-2697" data-mp="1s46ldlnrc4q0">
                ServiceNow Governance & Platform Stewardship
              </div>
              <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-6 text-white" data-id="element-2698" data-mp="1x419jhg5eg7d">
                ServiceNow Governance + Expert Development:{' '}
                <span className="text-nrs-orange" data-id="element-2699" data-mp="2b29di9blqhs1">The Complete Solution</span>
              </h1>
              <p className="text-xl text-nrs-light mb-8" data-id="element-2700" data-mp="271fpzh22n4zo">
                ServiceNow Impact drives product vision. New River provides both
                strategic governance AND expert development. Whether you need
                independent oversight of your SI or a trusted partner to build
                and operate your platform—we deliver both.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-2701" data-mp="2pthxo2wqjaxs">
                <a href="mailto:info@newriversystems.com" className="btn-primary" data-id="element-2702" data-mp="2pqvro7pht3ho">
                  Schedule a Free Platform Assessment
                </a>
                <Link to="/services/servicenow/calculator" className="btn-secondary" data-id="element-2703" data-mp="2zazz9pg2h1x8">
                  Calculate Your Technical Debt Risk Score
                </Link>
              </div>
            </motion.div>

            {/* Diagram */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="flex flex-col items-center gap-2" data-id="element-2704" data-mp="2q9a88kq3k4zz">
              {/* ServiceNow Impact */}
              <div className="w-full max-w-sm border-2 border-blue-400 rounded-lg p-4 text-center" data-id="element-2705" data-mp="391uv9lj47fvv">
                <div className="text-blue-300 font-bold text-sm" data-id="element-2706" data-mp="22l919g8kgwmo">
                  ServiceNow Impact
                </div>
                <div className="text-blue-400 text-xs mt-1" data-id="element-2707" data-mp="1xled3kg6el5b">
                  Product Vision & Adoption Strategy
                </div>
              </div>
              <ArrowDownIcon className="h-6 w-6 text-nrs-orange" data-id="element-2708" data-mp="d2keqiwf0kta" />
              {/* New River Box */}
              <div className="w-full max-w-sm bg-nrs-blue rounded-lg p-4" data-id="element-2709" data-mp="m53cwpp9a997">
                <div className="text-white font-bold text-sm text-center mb-3" data-id="element-2710" data-mp="m2yz9g1k1vvq">
                  New River: Governance + Development
                </div>
                <div className="grid grid-cols-2 gap-3" data-id="element-2711" data-mp="vvo7m21t6tmu">
                  <div className="bg-white/10 rounded p-3" data-id="element-2712" data-mp="vx91dpgau8jn">
                    <div className="text-nrs-orange text-xs font-bold uppercase mb-2" data-id="element-2713" data-mp="10soicq9lskka">
                      Governance Layer
                    </div>
                    <ul className="text-blue-100 text-xs space-y-1" data-id="element-2714" data-mp="19ue69m4a11db">
                      <li data-id="element-2715" data-mp="19sjv2zit4nmf">• Technical Policy</li>
                      <li data-id="element-2716" data-mp="37l7dn6hre3mp">• Data Governance</li>
                      <li data-id="element-2717" data-mp="37mign43dr7cr">• Security/ATO</li>
                      <li data-id="element-2718" data-mp="3blhi2epwklzg">• Strategic Governance</li>
                    </ul>
                  </div>
                  <div className="bg-white/10 rounded p-3" data-id="element-2719" data-mp="ir0w7msper81">
                    <div className="text-nrs-orange text-xs font-bold uppercase mb-2" data-id="element-2720" data-mp="itdksisyvzu2">
                      Development Services
                    </div>
                    <ul className="text-blue-100 text-xs space-y-1" data-id="element-2721" data-mp="rta03ttgvmcg">
                      <li data-id="element-2722" data-mp="nqvn3qchbgt3">• Platform Build</li>
                      <li data-id="element-2723" data-mp="np2dq3aheihe">• Module Dev</li>
                      <li data-id="element-2724" data-mp="11q506h5e1usu">• Operations</li>
                      <li data-id="element-2725" data-mp="11p3ms60wgw7y">• App Rationalization</li>
                    </ul>
                  </div>
                </div>
              </div>
              <ArrowDownIcon className="h-6 w-6 text-nrs-orange" data-id="element-2726" data-mp="wph9jwl2nfz5" />
              {/* SI Box */}
              <div className="w-full max-w-sm border-2 border-gray-500 rounded-lg p-4 text-center" data-id="element-2727" data-mp="1bi0zfvj7nqyn">
                <div className="text-gray-300 font-bold text-sm" data-id="element-2728" data-mp="16jhbxqoyy746">
                  OR: Your Existing SI
                </div>
                <div className="text-gray-400 text-xs mt-1" data-id="element-2729" data-mp="1fibgq4cge3st">
                  We govern their work
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* VALUE PROPOSITION */}
      <section className="section-light-blue" data-id="element-2730" data-mp="24264l6nxptvq">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-2731" data-mp="1q36r8v5vh1ev">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-12" data-id="element-2732" data-mp="1q4zndgiv038m">
            <h2 className="text-3xl font-bold mb-4" data-id="element-2733" data-mp="1l4lq8tisqdpd">
              Two Capabilities. One Partner.
            </h2>
            <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-2734" data-mp="16gqxjd4sg9ca"></div>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg" data-id="element-2735" data-mp="1bescblv6lmhy">
              Most organizations need both governance and development. New River
              delivers both under one roof.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-2736" data-mp="12f5cv5m0iqrj">
            {/* Governance */}
            <div data-id="element-2737" data-mp="ueh7qxa6fass">
              <div className="flex items-center gap-3 mb-5" data-id="element-2738" data-mp="pdtioh5id417">
                <div className="h-8 w-1 bg-nrs-orange rounded-full" data-id="element-2739" data-mp="pebmpzyewmjo" />
                <h3 className="font-bold text-xl text-nrs-navy" data-id="element-2740" data-mp="klrzscdu4f27">
                  Governance Services
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2741" data-mp="gj5b2kcgna98">
                {governanceCards.map((card, i) => <motion.div key={card.title} custom={i} initial="hidden" whileInView="visible" viewport={{
                once: true
              }} variants={fadeUp} className="card p-5" data-id="element-2742" data-mp="bjshlgr334xo" data-mpi={i}>
                    <div className="mb-3" data-id="element-2743" data-mp="1q1xeo1zpgns">{card.icon}</div>
                    <h4 className="font-semibold text-sm text-gray-900 mb-1" data-id="element-2744" data-mp="3tst8xvyxcom7">
                      {card.title}
                    </h4>
                    <p className="text-gray-500 text-xs leading-relaxed" data-id="element-2745" data-mp="1vzw000b0mzfp">
                      {card.desc}
                    </p>
                  </motion.div>)}
              </div>
            </div>

            {/* Development */}
            <div data-id="element-2746" data-mp="3ewl42whxt9ud">
              <div className="flex items-center gap-3 mb-5" data-id="element-2747" data-mp="3ew2yvadarlab">
                <div className="h-8 w-1 bg-nrs-blue rounded-full" data-id="element-2748" data-mp="3jntdhsj9ov7j" />
                <h3 className="font-bold text-xl text-nrs-navy" data-id="element-2749" data-mp="3jomnr3hfq2uf">
                  Development Services
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2750" data-mp="2qzff3bo9nwvg">
                {developmentCards.map((card, i) => <motion.div key={card.title} custom={i} initial="hidden" whileInView="visible" viewport={{
                once: true
              }} variants={fadeUp} className="card p-5" data-id="element-2751" data-mp="2vxqq3pqasdf9" data-mpi={i}>
                    <div className="mb-3" data-id="element-2752" data-mp="34yos62h6eav7">{card.icon}</div>
                    <h4 className="font-semibold text-sm text-gray-900 mb-1" data-id="element-2753" data-mp="350897vanopy1">
                      {card.title}
                    </h4>
                    <p className="text-gray-500 text-xs leading-relaxed" data-id="element-2754" data-mp="k8g18pdi6uwe">
                      {card.desc}
                    </p>
                  </motion.div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM SECTION */}
      <section className="section-white" data-id="element-2755" data-mp="2cclmkh35b8de">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-2756" data-mp="2qaaa5wolktm5">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-4" data-id="element-2757" data-mp="2laxjt1yip4et">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-2758" data-mp="1sn1g3daj6c8j">
              ServiceNow Impact Has Strategy Covered.
              <br data-id="element-2759" data-mp="27bxya36ax5mv" />
              <span className="text-nrs-orange" data-id="element-2760" data-mp="27ch3qkerm1k0">
                You Still Need Governance.
              </span>
            </h2>
            <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-2761" data-mp="1euvd6swe21r1"></div>
            <p className="text-gray-600 max-w-3xl mx-auto text-lg" data-id="element-2762" data-mp="1essb3ahyoi5y">
              ServiceNow Impact provides excellent product-line vision, adoption
              roadmaps, and strategic planning. But that's not governance. Most
              organizations face four critical governance gaps:
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10" data-id="element-2763" data-mp="3ciz6eutikdlm">
            {governanceGaps.map((gap, i) => <motion.div key={gap.title} custom={i} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="border border-red-100 rounded-lg p-6 bg-red-50" data-id="element-2764" data-mp="3gjj3jye37h7r" data-mpi={i}>
                <h3 className="font-bold text-base text-gray-900 mb-4" data-id="element-2765" data-mp="d29a5na35nkn">
                  {gap.title}
                </h3>
                <ul className="space-y-2" data-id="element-2766" data-mp="3v8nlrfkvtlfv">
                  {gap.items.map(item => <li key={item} className="flex items-start gap-2 text-sm text-gray-700" data-id="element-2767" data-mp="41uj07afxe98">
                      <XCircleIcon className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" data-id="element-2768" data-mp="2jtigc3td98sk" />
                      {item}
                    </li>)}
                </ul>
              </motion.div>)}
          </div>

          {/* The Result */}
          <div className="mt-12" data-id="element-2769" data-mp="3fkhr21120bv0">
            <h3 className="font-bold text-xl text-nrs-navy text-center mb-6" data-id="element-2770" data-mp="3bhcusg7kh6et">
              The Result
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-id="element-2771" data-mp="36hqge3g05k4f">
              {consequences.map((c, i) => <motion.div key={c.label} custom={i} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUp} className="bg-gray-50 rounded-lg p-4 text-center border border-gray-200" data-id="element-2772" data-mp="31jyld0k29hw0" data-mpi={i}>
                  <div className="text-3xl mb-2" data-id="element-2773" data-mp="3u8mcvdvel3i0">{c.icon}</div>
                  <div className="font-semibold text-sm text-gray-900 mb-1" data-id="element-2774" data-mp="3pg2pjp6wyu00">
                    {c.label}
                  </div>
                  <p className="text-gray-500 text-xs leading-relaxed" data-id="element-2775" data-mp="3khrghgog0niu">
                    {c.desc}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* SOLUTION SECTION */}
      <section className="section-navy" data-id="element-2776" data-mp="ocpapk8jzdcc">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-2777" data-mp="oe0dphu6ch2e">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-2778" data-mp="taqu669w5eoc">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-2779" data-mp="13ahni4mptnmu">
              ServiceNow Impact + New River ={' '}
              <span className="text-nrs-orange" data-id="element-2780" data-mp="1798ncjikfjdn">Complete Success</span>
            </h2>
            <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-2781" data-mp="1c8lbd7l6awht"></div>
            <p className="text-xl text-nrs-light max-w-2xl mx-auto" data-id="element-2782" data-mp="1h2ftw7p1l5fb">
              We provide both strategic governance AND expert ServiceNow
              development. Choose your engagement model:
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-id="element-2783" data-mp="1qxhfwuo0trdo">
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="card p-6" data-id="element-2784" data-mp="1v15o9fkh8qzz">
              <div className="text-nrs-blue font-bold text-sm uppercase tracking-wider mb-2" data-id="element-2785" data-mp="2zfb08cmkzy9b">
                Option 1
              </div>
              <h3 className="font-bold text-lg !text-gray-900 mb-2" data-id="element-2786" data-mp="34dw0ud5qwjya">
                Governance Only
              </h3>
              <p className="!text-gray-700 text-sm" data-id="element-2787" data-mp="38efydhu8dpky">
                Oversight of your existing SI. We validate, review, and govern
                their work.
              </p>
            </motion.div>
            <motion.div custom={1} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="card p-6" data-id="element-2788" data-mp="3icm09zg1evpt">
              <div className="text-nrs-blue font-bold text-sm uppercase tracking-wider mb-2" data-id="element-2789" data-mp="3s48gybb59mzy">
                Option 2
              </div>
              <h3 className="font-bold text-lg !text-gray-900 mb-2" data-id="element-2790" data-mp="5021cqmfick6">
                Development Only
              </h3>
              <p className="!text-gray-700 text-sm" data-id="element-2791" data-mp="1pimc2b8mahto">
                We build, you govern. For organizations with strong internal
                governance capability.
              </p>
            </motion.div>
            <motion.div custom={2} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="bg-nrs-orange rounded-xl p-6 relative shadow-lg" data-id="element-2792" data-mp="1ggmvfk1ilfr9">
              <div className="absolute top-3 right-3 bg-white text-nrs-orange text-xs font-bold px-2 py-0.5 rounded-full" data-id="element-2793" data-mp="287j5ye03r8bd">
                Most Popular
              </div>
              <div className="text-white font-bold text-sm uppercase tracking-wider mb-2" data-id="element-2794" data-mp="1zg0qy6ew6yjv">
                Option 3
              </div>
              <h3 className="font-bold text-lg text-white mb-2" data-id="element-2795" data-mp="1ugee3y2z7kbl">
                Governance + Development
              </h3>
              <p className="text-orange-100 text-sm" data-id="element-2796" data-mp="2rxwbrxpcjroc">
                Complete solution. Self-governing development with built-in
                architecture standards.
              </p>
            </motion.div>
          </div>

          {/* Comparison Table */}
          <div className="bg-white/5 rounded-xl overflow-hidden border border-white/10" data-id="element-2797" data-mp="3b0565t0q4ceh">
            <div className="grid grid-cols-2 bg-white/10" data-id="element-2798" data-mp="2xal8v2pww7ck">
              <div className="px-6 py-3 font-bold text-sm text-red-300" data-id="element-2799" data-mp="2x7z1p46xntuv">
                ❌ Traditional Approach
              </div>
              <div className="px-6 py-3 font-bold text-sm text-green-300" data-id="element-2800" data-mp="2nb2q2plqmrm3">
                ✅ New River Model
              </div>
            </div>
            {comparisonRows.map((row, i) => <div key={i} className={`grid grid-cols-2 ${i % 2 === 0 ? 'bg-white/5' : ''}`} data-id="element-2801" data-mp="2eaxkml03u3ak" data-mpi={i}>
                <div className="px-6 py-4 text-sm text-red-200 border-r border-white/10" data-id="element-2802" data-mp="2icj3qo38fdt7">
                  {row.traditional}
                </div>
                <div className="px-6 py-4 text-sm text-green-200" data-id="element-2803" data-mp="ckm1aucs40ah">
                  {row.nrs}
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* ENGAGEMENT MODELS */}
      <section className="section-white" data-id="element-2804" data-mp="213q9jjngdb6n">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-2805" data-mp="1x1lld0frx6re">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-2806" data-mp="1wziivgxfpl5s">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-2807" data-mp="2kr63xi80m205">
              Three Engagement Models
            </h2>
            <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-2808" data-mp="2fsmf9a21ec43"></div>
            <p className="text-gray-600 text-lg" data-id="element-2809" data-mp="2trvyyjpgboct">
              Choose the model that fits your organization's needs
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-2810" data-mp="2otata4rfkcgz">
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="card p-6" data-id="element-2811" data-mp="zd1ookjzzno9">
              <div className="text-nrs-orange font-bold text-xs uppercase tracking-wider mb-2" data-id="element-2812" data-mp="uh4jnae6q3qs">
                Model 1
              </div>
              <h3 className="font-bold text-lg text-nrs-navy mb-4" data-id="element-2813" data-mp="10u8o8gb07obg">
                Governance Oversight
              </h3>
              <div className="space-y-3 mb-5" data-id="element-2814" data-mp="15umq1gm0i211">
                <div className="flex gap-2 text-sm" data-id="element-2815" data-mp="1asxzhq8eaaiq">
                  <span className="text-blue-500 font-semibold w-24 flex-shrink-0" data-id="element-2816" data-mp="1aso941jfu1ci">
                    Impact:
                  </span>
                  <span className="text-gray-600" data-id="element-2817" data-mp="i4imtacqk4ak">Product Vision</span>
                </div>
                <div className="flex gap-2 text-sm" data-id="element-2818" data-mp="i269jahxai3u">
                  <span className="text-gray-500 font-semibold w-24 flex-shrink-0" data-id="element-2819" data-mp="n2tvviv7iarq">
                    Your SI:
                  </span>
                  <span className="text-gray-600" data-id="element-2820" data-mp="rw567tz899oe">Implementation</span>
                </div>
                <div className="flex gap-2 text-sm" data-id="element-2821" data-mp="3qi7zt8d2yirv">
                  <span className="text-nrs-orange font-semibold w-24 flex-shrink-0" data-id="element-2822" data-mp="3vidku5ouh70z">
                    New River:
                  </span>
                  <span className="text-gray-600" data-id="element-2823" data-mp="3nxtiz8scfn04">
                    Governance (technical policy, data, security/ATO, strategic)
                  </span>
                </div>
              </div>
              <div className="bg-nrs-light rounded-lg p-3" data-id="element-2824" data-mp="3e3t6umyn9hhw">
                <div className="text-xs font-semibold text-nrs-blue mb-1" data-id="element-2825" data-mp="391lptiyyjb8p">
                  Best For:
                </div>
                <p className="text-xs text-gray-600" data-id="element-2826" data-mp="352lc2evc6ysk">
                  Organizations with existing SI relationships who need
                  independent validation
                </p>
              </div>
            </motion.div>

            <motion.div custom={1} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="card p-6" data-id="element-2827" data-mp="2qc5ncq3pp8pn">
              <div className="text-nrs-orange font-bold text-xs uppercase tracking-wider mb-2" data-id="element-2828" data-mp="2lcjawivpjwhw">
                Model 2
              </div>
              <h3 className="font-bold text-lg text-nrs-navy mb-4" data-id="element-2829" data-mp="26qc5p84k5ngs">
                Development Services
              </h3>
              <div className="space-y-3 mb-5" data-id="element-2830" data-mp="2bpp3gnwywey5">
                <div className="flex gap-2 text-sm" data-id="element-2831" data-mp="2bncbdhxhwo7d">
                  <span className="text-blue-500 font-semibold w-24 flex-shrink-0" data-id="element-2832" data-mp="1xqfo1qgl5ov8">
                    Impact:
                  </span>
                  <span className="text-gray-600" data-id="element-2833" data-mp="22p0pft7kqel9">Product Vision</span>
                </div>
                <div className="flex gap-2 text-sm" data-id="element-2834" data-mp="2qeuz61wljn1a">
                  <span className="text-nrs-orange font-semibold w-24 flex-shrink-0" data-id="element-2835" data-mp="2ugg7rb23lf5o">
                    New River:
                  </span>
                  <span className="text-gray-600" data-id="element-2836" data-mp="2go0bct3pcfdq">
                    Implementation (platform dev, integrations, operations)
                  </span>
                </div>
                <div className="flex gap-2 text-sm" data-id="element-2837" data-mp="39cxlua1o97vx">
                  <span className="text-gray-500 font-semibold w-24 flex-shrink-0" data-id="element-2838" data-mp="39bco6bk298tu">
                    Your Team:
                  </span>
                  <span className="text-gray-600" data-id="element-2839" data-mp="3nhxs7o57yucw">Governance</span>
                </div>
              </div>
              <div className="bg-nrs-light rounded-lg p-3" data-id="element-2840" data-mp="3jg2usuqet14e">
                <div className="text-xs font-semibold text-nrs-blue mb-1" data-id="element-2841" data-mp="134z500ngug1">
                  Best For:
                </div>
                <p className="text-xs text-gray-600" data-id="element-2842" data-mp="3s963kpq40psp">
                  Organizations with strong internal governance capability
                  needing development capacity
                </p>
              </div>
            </motion.div>

            <motion.div custom={2} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="border-2 border-nrs-orange rounded-xl p-6 relative shadow-lg bg-white" data-id="element-2843" data-mp="30k2yj7qefbf0">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-nrs-orange text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap" data-id="element-2844" data-mp="39law1nl3c881">
                ⭐ Most Popular
              </div>
              <div className="text-nrs-orange font-bold text-xs uppercase tracking-wider mb-2" data-id="element-2845" data-mp="2j8xsm7vgfunu">
                Model 3
              </div>
              <h3 className="font-bold text-lg text-nrs-navy mb-4" data-id="element-2846" data-mp="2nbklmc3v3lmm">
                Governance + Development
              </h3>
              <div className="space-y-3 mb-5" data-id="element-2847" data-mp="2s8b2h1nhql93">
                <div className="flex gap-2 text-sm" data-id="element-2848" data-mp="2x0vtxwixcm9g">
                  <span className="text-blue-500 font-semibold w-24 flex-shrink-0" data-id="element-2849" data-mp="2x2f9tm0o4vap">
                    Impact:
                  </span>
                  <span className="text-gray-600" data-id="element-2850" data-mp="36y9zv2781sni">Product Vision</span>
                </div>
                <div className="flex gap-2 text-sm" data-id="element-2851" data-mp="3b0d9rme8sp6o">
                  <span className="text-nrs-orange font-semibold w-24 flex-shrink-0" data-id="element-2852" data-mp="3b0n0xdb0x2dy">
                    New River:
                  </span>
                  <span className="text-gray-600" data-id="element-2853" data-mp="1gm42bltlrj17">
                    BOTH Governance AND Implementation
                  </span>
                </div>
              </div>
              <div className="bg-orange-50 rounded-lg p-3 mb-4" data-id="element-2854" data-mp="1ln9spczsiu7k">
                <div className="text-xs font-semibold text-nrs-orange mb-1" data-id="element-2855" data-mp="1e2ppabnn524l">
                  Best For:
                </div>
                <p className="text-xs text-gray-600" data-id="element-2856" data-mp="199mww6ulnypz">
                  Small-to-medium organizations needing complete platform
                  stewardship
                </p>
              </div>
              <p className="text-xs text-gray-500 italic" data-id="element-2857" data-mp="zel5t5grkmkr">
                Self-governing development: every project follows our own
                architecture standards, data governance, and ATO requirements
              </p>
            </motion.div>
          </div>

          {/* App Rationalization Callout */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mt-10 border-l-4 border-nrs-orange bg-orange-50 rounded-r-xl p-6" data-id="element-2858" data-mp="1n475du11t3ta">
            <h3 className="font-bold text-lg text-nrs-navy mb-3" data-id="element-2859" data-mp="7kssa8rh1f5c">
              The Application Rationalization Differentiator
            </h3>
            <p className="text-gray-700 text-sm mb-4" data-id="element-2860" data-mp="2xj9mjdufriro">
              Unlike SIs who want to migrate everything to ServiceNow, we
              provide independent assessment of your application portfolio:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-2861" data-mp="37gxfpzce8i7q">
              <div className="flex items-start gap-2 text-sm text-gray-700" data-id="element-2862" data-mp="37fmcc0mv1ch5">
                <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-2863" data-mp="3l70yggc2dtsf" />
                Which applications are a strong ServiceNow fit (we'll build
                these)
              </div>
              <div className="flex items-start gap-2 text-sm text-gray-700" data-id="element-2864" data-mp="3v4xg7qzibmzi">
                <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-2865" data-mp="3q8ggs0muxm74" />
                Which should remain standalone (don't force it)
              </div>
              <div className="flex items-start gap-2 text-sm text-gray-700" data-id="element-2866" data-mp="1vup8p5k6fads">
                <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-2867" data-mp="bc4z5o9q5b5v" />
                Which can be decommissioned entirely (cost savings)
              </div>
              <div className="flex items-start gap-2 text-sm text-gray-700" data-id="element-2868" data-mp="gcsqyc5s1w15">
                <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-2869" data-mp="1lsabjuvmvtx" />
                What the ROI and implementation sequence should be
              </div>
            </div>
            <p className="text-sm text-nrs-orange font-semibold mt-4" data-id="element-2870" data-mp="3opghzanki3ly">
              Outcome: You invest in ServiceNow migrations that deliver 3:1 ROI,
              not projects that create technical debt.
            </p>
          </motion.div>
        </div>
      </section>

      {/* PROOF POINTS */}
      <section className="section-light-blue" data-id="element-2871" data-mp="3jrx6gii984xp">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-2872" data-mp="1d0pbtt88zdi6">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-10" data-id="element-2873" data-mp="1cze9zyyd4ftp">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-2874" data-mp="1bkbkz3zrgcfr">
              What Good Governance Delivers
            </h2>
            <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-2875" data-mp="16m062eulzbqo"></div>
            <p className="text-gray-600 text-lg" data-id="element-2876" data-mp="12k589kbvzghn">
              Client outcomes from recent engagements
            </p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2877" data-mp="1z8upr9jlq733">
            {proofPoints.map((p, i) => <motion.div key={p.metric} custom={i} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="card p-5" data-id="element-2878" data-mp="1ucmg2bo6b63b" data-mpi={i}>
                <div className="text-nrs-orange font-bold text-lg mb-1" data-id="element-2879" data-mp="1lhpwqqaqod1q">
                  {p.result}
                </div>
                <div className="text-gray-600 text-xs" data-id="element-2880" data-mp="2i28aymxiiz5i">{p.metric}</div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* WHO WE SERVE */}
      <section className="section-white" data-id="element-2881" data-mp="3vxgp2inhn7id">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-2882" data-mp="4puioi9dwqxt">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start" data-id="element-2883" data-mp="4q48o5ufiy3i">
            <div data-id="element-2884" data-mp="8qo7reykcbsa">
              <h2 className="text-3xl font-bold mb-6" data-id="element-2885" data-mp="8q4n3qhj39ak">Who We Serve</h2>
              <div className="space-y-3" data-id="element-2886" data-mp="drlp5pbr11sq">
                <div className="flex items-start gap-3 text-sm text-gray-700" data-id="element-2887" data-mp="do666beyf8m1">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-2888" data-mp="ipdeqcox8btr" />
                  Organizations with 500 to 500,000 ServiceNow users
                </div>
                <div className="flex items-start gap-3 text-sm text-gray-700" data-id="element-2889" data-mp="sgq8j9ua5c2f">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-2890" data-mp="wha5acaxydo1" />
                  State/local governments and higher education institutions
                </div>
                <div className="flex items-start gap-3 text-sm text-gray-700" data-id="element-2891" data-mp="16eeqo9wyq53s">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-2892" data-mp="ss7m8ammlnxt" />
                  Organizations expanding beyond ITSM into HRSD, Security
                  Operations, or Strategic Portfolio Management
                </div>
                <div className="flex items-start gap-3 text-sm text-gray-700" data-id="element-2893" data-mp="12hr71ltwfrvb">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-2894" data-mp="xk8vroset0k5" />
                  Platforms stuck on old versions due to heavy customization
                </div>
                <div className="flex items-start gap-3 text-sm text-gray-700" data-id="element-2895" data-mp="4vbpyl5dww8a">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-2896" data-mp="jrj3x3q791f4" />
                  Agencies under budget scrutiny needing to demonstrate
                  ServiceNow ROI
                </div>
              </div>
            </div>
            <div data-id="element-2897" data-mp="1v54vswng1h4c">
              <h3 className="font-bold text-xl text-nrs-navy mb-5" data-id="element-2898" data-mp="1v8sqw08jebl6">
                Recent Engagements
              </h3>
              <div className="space-y-4" data-id="element-2899" data-mp="1xrnnxz8ese6e">
                <div className="border border-nrs-blue/20 rounded-xl p-5 bg-nrs-light" data-id="element-2900" data-mp="1xpt30kx50m2b">
                  <div className="font-bold text-nrs-blue text-sm mb-1" data-id="element-2901" data-mp="22ra2qd3vy2hb">
                    Defense Client
                  </div>
                  <p className="text-gray-600 text-sm" data-id="element-2902" data-mp="1osskguwr1i6s">
                    Secure on-premise platform governance — cross-instance data
                    synchronization and ATO compliance
                  </p>
                </div>
                <div className="border border-nrs-blue/20 rounded-xl p-5 bg-nrs-light" data-id="element-2903" data-mp="2lgozvwgga7gf">
                  <div className="font-bold text-nrs-blue text-sm mb-1" data-id="element-2904" data-mp="27pk1sysqdn87">
                    Defense Educational Institution
                  </div>
                  <p className="text-gray-600 text-sm" data-id="element-2905" data-mp="2bpucupvdr3tx">
                    Academic institution platform stewardship — governance
                    framework and application rationalization
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-light-blue" data-id="element-2906" data-mp="3e13xr2xlsvmw">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-2907" data-mp="3a2whsaayrycr">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-10" data-id="element-2908" data-mp="352i9cr5game7">
            <h2 className="text-3xl font-bold mb-4" data-id="element-2909" data-mp="2v4ug65nhtmy5">
              Frequently Asked Questions
            </h2>
            <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-2910" data-mp="2qd2jdddqukzk"></div>
          </motion.div>
          <div className="space-y-3 max-w-4xl mx-auto" data-id="element-2911" data-mp="2md23doazy1qe">
            {faqs.map(faq => <FAQItem key={faq.q} q={faq.q} a={faq.a} data-id="element-2912" data-mp="2hdz0a9eku5l2" />)}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section-navy" data-id="element-2913" data-mp="2ccrv8i3tjki3">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15 text-center" data-id="element-2914" data-mp="27f007f7vni9o">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-2915" data-mp="27h2y0lf9ufoy">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-2916" data-mp="22c8zodonl0zr">
              Ready to Close the Governance Gap?
            </h2>
            <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-8" data-id="element-2917" data-mp="2c8vjr095no0q"></div>
            <p className="text-xl text-nrs-light mb-8 max-w-2xl mx-auto" data-id="element-2918" data-mp="1eklca5qlo828">
              Let's talk about your platform. Free 30-minute assessment, no
              commitment required.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2919" data-mp="1oj17okwnu3hj">
              <a href="mailto:info@newriversystems.com" className="btn-primary" data-id="element-2920" data-mp="1ogojhq0b6ww1">
                Schedule a Free Assessment
              </a>
              <a href="https://drive.google.com/file/d/1J8g2FBnUX_0SU3OsQdAGsk0fzTrcJbak/view?usp=drive_link" target="_blank" rel="noopener noreferrer" className="btn-secondary" data-id="element-2921" data-mp="vuvmxy5lwehp">
                Download Governance Guide
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}